import { FunctionComponent, useRef, useState, useCallback, memo } from 'react';
import styles from '../../styles/components/image/ImageFieldPanel.module.css';

interface ImageFieldPanelProps {
  value: string;
  onChange: (value: string) => void;
  onGenerate: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSettingsClick: (anchorRect?: DOMRect) => void;
  onImageUpload: (file: File | null) => void;
  uploadedImage: File | null;
  disabled?: boolean;
}

const ImageFieldPanel: FunctionComponent<ImageFieldPanelProps> = memo(({
  value,
  onChange,
  onGenerate,
  onKeyDown,
  onSettingsClick,
  onImageUpload,
  uploadedImage,
  disabled = false
}) => {
  const settingsBtnRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    // Clear previous errors
    setFileError(null);
    
    if (!file) {
      handleRemoveImage();
      return;
    }

    setIsProcessingFile(true);

    try {
      // Use the service validation for consistency
      const { imageGenerationService } = require('../../services/imageGenerationService');
      imageGenerationService.validateImageFile(file);

      // Create preview URL with error handling
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          setImagePreviewUrl(result);
          onImageUpload(file);
          setFileError(null);
        } else {
          setFileError('Failed to load image preview');
          onImageUpload(null);
        }
        setIsProcessingFile(false);
      };
      
      reader.onerror = () => {
        console.error('Failed to read image file');
        setFileError('Failed to read the image file. Please try a different file.');
        setImagePreviewUrl(null);
        onImageUpload(null);
        setIsProcessingFile(false);
      };
      
      reader.onabort = () => {
        setFileError('File reading was cancelled. Please try again.');
        setImagePreviewUrl(null);
        onImageUpload(null);
        setIsProcessingFile(false);
      };
      
      // Set timeout for file reading
      const timeout = setTimeout(() => {
        reader.abort();
        setFileError('File reading timed out. Please try a smaller image.');
        setImagePreviewUrl(null);
        onImageUpload(null);
        setIsProcessingFile(false);
      }, 10000); // 10 second timeout
      
      reader.onloadend = () => {
        clearTimeout(timeout);
      };
      
      reader.readAsDataURL(file);
      
    } catch (error: any) {
      console.error('File validation failed:', error);
      
      // Set user-friendly error message
      const errorMessage = error?.message || 'Invalid file. Please select a valid image file.';
      setFileError(errorMessage);
      setImagePreviewUrl(null);
      onImageUpload(null);
      setIsProcessingFile(false);
      
      // Clear the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleAddImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setImagePreviewUrl(null);
    setFileError(null);
    setIsProcessingFile(false);
    onImageUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClearFileError = useCallback(() => {
    setFileError(null);
  }, []);

  // Handle keyboard navigation for buttons
  const handleButtonKeyDown = useCallback((e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  }, []);

  // Handle input focus for better accessibility
  const handleInputFocus = useCallback(() => {
    // Clear any file errors when user focuses on input
    if (fileError) {
      setFileError(null);
    }
  }, [fileError]);

  return (
    <div className={styles.fieldPanel} role="region" aria-label="Image generation input">
      <div className={styles.fieldPanel1} ref={anchorRef}>
        <div className={styles.fieldText}>
          <label htmlFor="prompt-input" className="sr-only">
            Image generation prompt
          </label>
          <input
            id="prompt-input"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={handleInputFocus}
            placeholder="Describe the image you want to generate..."
            className={styles.text}
            disabled={disabled}
            aria-describedby={fileError ? 'file-error' : 'input-help'}
            aria-invalid={!!fileError}
          />
        </div>
        
        {/* Image Preview Area */}
        {imagePreviewUrl && (
          <div className={styles.imagePreview} role="img" aria-label="Uploaded image preview">
            <img 
              src={imagePreviewUrl} 
              alt={`Preview of uploaded image: ${uploadedImage?.name || 'uploaded file'}`}
              className={styles.previewImage}
            />
            <button 
              className={styles.removeImageBtn}
              onClick={handleRemoveImage}
              onKeyDown={(e) => handleButtonKeyDown(e, handleRemoveImage)}
              type="button"
              aria-label="Remove uploaded image"
              title="Remove uploaded image"
            >
              <img src="/icons/close.svg" alt="" className={styles.removeIcon} />
            </button>
          </div>
        )}

        {/* File Processing Indicator */}
        {isProcessingFile && (
          <div className={styles.fileProcessing} role="status" aria-live="polite">
            <div className={styles.processingSpinner} aria-hidden="true"></div>
            <span className={styles.processingText}>Processing image...</span>
          </div>
        )}

        {/* File Error Display */}
        {fileError && (
          <div className={styles.fileError} role="alert" id="file-error">
            <div className={styles.fileErrorMessage}>{fileError}</div>
            <button 
              className={styles.fileErrorDismiss}
              onClick={handleClearFileError}
              onKeyDown={(e) => handleButtonKeyDown(e, handleClearFileError)}
              type="button"
              aria-label="Dismiss error message"
              title="Dismiss error message"
            >
              <img src="/icons/close.svg" alt="" className={styles.dismissIcon} />
            </button>
          </div>
        )}

        <div className={styles.frameParent}>
          <div className={styles.tagsParent}>
            <button
              className={styles.tags}
              onClick={() => {
                const rect = anchorRef.current?.getBoundingClientRect();
                onSettingsClick(rect);
              }}
              onKeyDown={(e) => handleButtonKeyDown(e, () => {
                const rect = anchorRef.current?.getBoundingClientRect();
                onSettingsClick(rect);
              })}
              type="button"
              aria-label="Open image generation settings"
              title="Open settings"
              disabled={disabled}
            >
              <img className={styles.leftIcon} alt="" src="/icons/chat/settings.svg" />
            </button>
          </div>
          <div className={styles.tagsGroup}>
            <button 
              className={`${styles.tags} ${uploadedImage ? styles.hasImage : ''}`} 
              onClick={handleAddImageClick}
              onKeyDown={(e) => handleButtonKeyDown(e, handleAddImageClick)}
              type="button"
              aria-label={uploadedImage ? 'Change uploaded image' : 'Add reference image'}
              title={uploadedImage ? 'Change image' : 'Add image'}
              disabled={disabled}
            >
              <img className={styles.leftIcon} alt="" src="/icons/chat/add_file.svg" />
              <div className={styles.testWrap}>
                <div className={styles.text1}>
                  {uploadedImage ? 'Change Image' : 'Add Image'}
                </div>
              </div>
            </button>
            <button 
              className={styles.button} 
              onClick={onGenerate}
              onKeyDown={(e) => handleButtonKeyDown(e, onGenerate)}
              type="button"
              aria-label="Generate images from prompt"
              title="Generate images"
              disabled={disabled || !value.trim()}
            >
              <div className={styles.textWrap}>
                <div className={styles.text1}>Generate</div>
              </div>
              <img className={styles.leftIcon} alt="" src="/icons/chevron_up.svg" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      />
      
      <div className={styles.text3} id="input-help">
        AI can make mistakes. We recommend checking important information
      </div>
    </div>
  );
});

ImageFieldPanel.displayName = 'ImageFieldPanel';

export default ImageFieldPanel;